import { useCallback } from 'react';
import { Grid } from '@interxfi/design-system/components/alignment/grid';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Button } from '@interxfi/design-system/components/button/button';
import { Modal, type ModalSimpleProps } from '@interxfi/design-system/components/dialog/modal/modal';
import { useMergedDialogOpenProps } from '@interxfi/design-system/components/dialog/utils';
import { Typography } from '@interxfi/design-system/components/typography';

interface ConfirmRemoveControlPersonAlertProps extends Omit<ModalSimpleProps, 'title' | 'description' | 'variant'> {
  handleRemoveControlPerson: VoidFunction;
}

export function ConfirmRemoveControlPersonAlert({
  handleRemoveControlPerson,
  isOpen: providedIsOpen,
  onOpenChange: providedOnOpenChange,
  ...rest
}: ConfirmRemoveControlPersonAlertProps) {
  const [isOpen, onOpenChange] = useMergedDialogOpenProps({ isOpen: providedIsOpen, onOpenChange: providedOnOpenChange });
  const handleConfirm = useCallback(() => {
    handleRemoveControlPerson();
    onOpenChange(false);
  }, [handleRemoveControlPerson, onOpenChange]);

  return (
    <Modal
      description="Confirm that you would like to remove the selected control person from the organization."
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title="Remove Control Person"
      variant="simple"
      {...rest}
    >
      <VerticalStack gap={7} inlineAlign="center">
        <Typography align="center" lineHeight="1.5rem" size="0.875rem">
          Are you sure you want to remove this control person from the organization?
        </Typography>

        <Grid autoColumns={2} gap={4}>
          <Button
            fullWidth
            onClick={() => {
              onOpenChange(false);
            }}
            size="large"
            variant="secondary"
          >
            Cancel
          </Button>

          <Button fullWidth onClick={handleConfirm} size="large" variant="danger">
            Yes, Remove
          </Button>
        </Grid>
      </VerticalStack>
    </Modal>
  );
}
